import type { MyWorkItem } from "@/stores/my-work-store"

// Pure helpers behind the shared work row (Home "Up next" + My Work).

/**
 * Router state a My Work row passes to the editor, so its breadcrumb can lead
 * back — to the same tab, filters and sort (`search`, without the leading `?`).
 */
export interface FromMyWorkState {
  from: 'my-work'
  search?: string
}

export function fromMyWorkState(search = ''): FromMyWorkState {
  return search ? { from: 'my-work', search } : { from: 'my-work' }
}

/** The My Work URL to return to, or null when the page wasn't opened from My Work. */
export function myWorkReturnPath(state: unknown): string | null {
  if (typeof state !== 'object' || state === null) return null
  const { from, search } = state as { from?: unknown; search?: unknown }
  if (from !== 'my-work') return null
  return typeof search === 'string' && search ? `/app/my-work?${search}` : '/app/my-work'
}

export function sectionEditorPath(item: Pick<MyWorkItem, 'bidId' | 'artifactId' | 'sectionKey'>): string {
  return `/app/bids/${item.bidId}/responses/${item.artifactId}/sections/${item.sectionKey}`
}

export type NextStep = 'Start' | 'Continue' | 'Review' | 'Open'

/** What opening the row lets the user do next. */
export function nextStepLabel(item: Pick<MyWorkItem, 'role' | 'collaborationStatus'>): NextStep {
  if (item.role === 'reviewer') return item.collaborationStatus === 'in_review' ? 'Review' : 'Open'
  if (item.collaborationStatus === 'not_started') return 'Start'
  if (item.collaborationStatus === 'in_progress' || item.collaborationStatus === 'changes_requested') return 'Continue'
  return 'Open'
}

/** `708 words`, `1,240 words`; null when nothing is drafted or the count is unknown. */
export function formatWordCount(wordCount: number | null | undefined): string | null {
  if (typeof wordCount !== 'number' || !Number.isFinite(wordCount) || wordCount <= 0) return null
  return `${wordCount.toLocaleString('en-AU')} ${wordCount === 1 ? 'word' : 'words'}`
}
